import { PropsWithChildren } from 'react';
import { TextButton } from '@buoyhealth/common.buoy-button';
import { ChevronLeft } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import useAppContext from 'src/hooks/useAppContext';

interface IResponsiveSurfaceProps extends PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  backText?: string;
  modalHeader?: string;
}

/**
 * Surface that slides in from the right while Polaris pop up is open,
 * or opens in a modal if Polaris app is closed (from embedded interviews).
 * */
const ResponsiveSurface = ({
  isOpen,
  onClose,
  children,
  backText,
  modalHeader,
}: IResponsiveSurfaceProps) => {
  const theme = useTheme();
  const { isOpen: isPolarisAppOpen } = useAppContext();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (isPolarisAppOpen) {
    return (
      <Slide in={isOpen} direction="left" mountOnEnter>
        <Paper
          sx={{
            width: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            backgroundColor: 'white',
            height: '100%',
            overflowY: 'scroll',
            zIndex: 10,
            paddingBottom: '1rem',
            borderRadius: 0,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {backText && (
            <Grid container padding={1} paddingY={2}>
              <Grid item>
                <TextButton
                  onClick={onClose}
                  className="!bp-text-xs !bp-text-gray-100 bp-underline"
                >
                  <ChevronLeft fontSize="small" /> {backText}
                </TextButton>
              </Grid>
            </Grid>
          )}
          {children}
        </Paper>
      </Slide>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <Paper
        sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', zIndex: 2147483647 }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>{modalHeader}</DialogTitle>
        <Divider />
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.gray[50],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0 }}>{children}</DialogContent>
      </Paper>
    </Dialog>
  );
};

export default ResponsiveSurface;
